import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ROUTES } from '@constants/index'
import { AppError, AppErrorType } from '@typesApp/AppError'
import { ViewName } from '@typesApp/ViewNavigation'
import { ViewState } from '@typesApp/ViewState'
import { AppState } from '@redux/store'

type ProgressStep = number

type VoidCallback = () => void
type VoidPromiseCallback = () => Promise<void>
type LoadingPageState = {
  action?: VoidPromiseCallback
  disableAutoClose?: boolean
}

type WorkflowState = {
  showHeader: boolean
  viewState: ViewState
  appVisible: boolean
  appVisibleAllowed: boolean
  appClosing: boolean
  step?: ProgressStep
  hideNavigateBack?: boolean
  onBeforeCloseCallback?: VoidPromiseCallback
  onNavigateBackCallback?: VoidCallback
  viewName?: ViewName
  loadingPage?: LoadingPageState
  appError?: AppError
  hideDrawerClose?: boolean
}

const DEFAULT_VIEW_STATE: ViewState = {
  isEditing: false,
  isReadonly: false,
}

const EDIT_VIEW_STATE: ViewState = {
  isEditing: true,
  isReadonly: false,
  previousRoute: ROUTES.REVIEW_CLAIM,
}

const READONLY_VIEW_STATE: ViewState = {
  isEditing: false,
  isReadonly: true,
}

const initialState: WorkflowState = {
  showHeader: true,
  viewState: DEFAULT_VIEW_STATE,
  appVisible: false,
  appVisibleAllowed: false,
  appClosing: false,
  hideNavigateBack: true,
}

const REDUCER_PATH = 'workflow'

export const workflowSlice = createSlice({
  name: REDUCER_PATH,
  initialState: initialState,
  reducers: {
    setProgressStep(state, action: PayloadAction<ProgressStep>) {
      state.showHeader = action.payload > 0 ? true : state.showHeader
      state.step = action.payload
    },
    hideProgressStep(state) {
      state.step = 0
    },
    hideDrawerHeader(state) {
      state.showHeader = false
    },
    hideDrawerClose(state) {
      state.hideDrawerClose = true
    },
    resetDrawerClose(state) {
      state.hideDrawerClose = undefined
    },
    showApp(state) {
      state.appVisible = true
    },
    closeApp(state) {
      if (state.appVisible) {
        state.appClosing = true
      }
    },
    setHideNavigateBack(state, action: PayloadAction<boolean>) {
      state.hideNavigateBack = action.payload
    },
    setNavigateBackCallback(state, action: PayloadAction<VoidCallback>) {
      state.onNavigateBackCallback = action.payload
    },
    setViewName(state, action: PayloadAction<ViewName | undefined>) {
      state.viewName = action.payload
    },
    setEditMode(state) {
      state.viewState = EDIT_VIEW_STATE
    },
    setReadonlyMode(state) {
      state.viewState = READONLY_VIEW_STATE
    },
    resetViewState(state) {
      state.viewState = DEFAULT_VIEW_STATE
    },
    setBeforeCloseCallback(state, action: PayloadAction<VoidPromiseCallback>) {
      state.onBeforeCloseCallback = action.payload
    },
    resetBeforeCloseCallback(state) {
      state.onBeforeCloseCallback = undefined
    },
    setLoadingPageAction(state, action: PayloadAction<LoadingPageState>) {
      state.loadingPage = action.payload
    },
    clearLoadingPageAction(state) {
      state.loadingPage = undefined
    },
    setAppError(state, action: PayloadAction<AppError | AppErrorType>) {
      if (typeof action.payload === 'object') {
        state.appError = action.payload
      } else {
        state.appError = {
          errorType: action.payload,
        }
      }
    },
    resetAppError(state) {
      state.appError = undefined
    },
    setAppVisibleAllowed(state, action: PayloadAction<boolean | undefined>) {
      state.appVisibleAllowed = action === undefined || action.payload === undefined ? true : action.payload
    },
    resetWorkflow(state) {
      return {
        ...initialState,
      }
    },
    resetWorkflowInitialState(state) {
      return initialState
    },
  },
})

export const {
  setProgressStep,
  hideProgressStep,
  hideDrawerHeader,
  hideDrawerClose,
  resetDrawerClose,
  showApp,
  closeApp,
  setEditMode,
  setReadonlyMode,
  resetViewState,
  setBeforeCloseCallback,
  resetBeforeCloseCallback,
  setAppVisibleAllowed,
  setHideNavigateBack,
  setNavigateBackCallback,
  setLoadingPageAction,
  clearLoadingPageAction,
  setViewName,
  setAppError,
  resetAppError,
  resetWorkflow,
  resetWorkflowInitialState,
} = workflowSlice.actions

export const drawerHeaderVisibleSelector = (state: AppState): boolean => state.workflow.showHeader
export const drawerHideCloseSelector = (state: AppState): boolean => state.workflow.hideDrawerClose === true
export const progressStepSelector = (state: AppState): ProgressStep | undefined => state.workflow.step
export const viewStateSelector = (state: AppState): ViewState => state.workflow.viewState
export const appErrorSelector = (state: AppState): AppError | undefined => state.workflow.appError
export const appVisibleSelector = (state: AppState): boolean => state.workflow.appVisible
export const appVisibleAllowedSelector = (state: AppState): boolean => state.workflow.appVisibleAllowed
export const appClosingSelector = (state: AppState): boolean => state.workflow.appClosing
export const appBeforeCloseCallbackSelector = (state: AppState): VoidPromiseCallback | undefined =>
  state.workflow.onBeforeCloseCallback
export const hideNavigateBackSelector = (state: AppState): boolean => Boolean(state.workflow.hideNavigateBack)
export const navigateBackCallbackSelector = (state: AppState): VoidCallback | undefined =>
  state.workflow.onNavigateBackCallback
export const viewNameSelector = (state: AppState): ViewName | undefined => state.workflow.viewName
export const loadingPageActionSelector = (state: AppState): LoadingPageState | undefined => state.workflow.loadingPage
